import {
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";

import {
  VISIT_HISTORY_LIMIT,
  visitsClient,
  type TodayVisit,
  type VisitHistoryCursor,
} from "./visitsClient";

export const visitsKeys = {
  all: ["visits"] as const,
  today: (sessionKey: string) => ["visits", sessionKey, "today"] as const,
  open: (sessionKey: string) => ["visits", sessionKey, "open"] as const,
  forPatient: (sessionKey: string, patientUuid: string) =>
    ["visits", sessionKey, "for-patient", patientUuid] as const,
};

/** Shared so suspense and non-suspense readers hit the same cache entry. */
const todayVisitsQueryOptions = (sessionKey: string) => ({
  queryKey: visitsKeys.today(sessionKey),
  queryFn: () => visitsClient.today(),
});

export const useTodayVisits = (sessionKey: string) =>
  useSuspenseQuery(todayVisitsQueryOptions(sessionKey));

export const useTodayVisitsQuery = (sessionKey: string, enabled: boolean) =>
  useQuery({ ...todayVisitsQueryOptions(sessionKey), enabled });

export const useOpenVisits = (sessionKey: string) =>
  useQuery({
    queryKey: visitsKeys.open(sessionKey),
    queryFn: () => visitsClient.open(),
  });

export const useBoardVisits = (sessionKey: string): TodayVisit[] => {
  const { data: today } = useTodayVisits(sessionKey);
  const { data: open } = useOpenVisits(sessionKey);
  return useMemo(() => [...today, ...(open ?? [])], [today, open]);
};

export const usePatientVisitHistory = (
  sessionKey: string,
  patientUuid: string,
) =>
  useInfiniteQuery({
    queryKey: visitsKeys.forPatient(sessionKey, patientUuid),
    queryFn: ({ pageParam }) => visitsClient.forPatient(patientUuid, pageParam),
    initialPageParam: undefined as VisitHistoryCursor | undefined,
    getNextPageParam: (lastPage: TodayVisit[]) => {
      if (lastPage.length < VISIT_HISTORY_LIMIT) return undefined;
      const last = lastPage[lastPage.length - 1];
      return { before: last.createdAt, beforeUuid: last.id };
    },
  });
